import services from "@/composable/services";
import { ref } from "vue";
import router from "@/router";
import { useRoute } from "vue-router";
import {
  GenericLocalApplicationResult,
  getGenericApplicationConfigFile,
  getGenericApplicationId,
  getGenericApplicationName,
  mustSignCharte,
} from "@/types/front/GenericLocalApplicationResult";

export interface FileUrl {
  url: string;
  filename: string;
  redirection: (() => void) | undefined;
}

/**
 * Fonctions "callback" attendues dans certains uploads
 *
 * Contrat minimal utilisé ici :
 * - parseChunck(chunk, source, errors, functions)
 * - fileUpload (payload passé au service)
 */
export type FileUploadFunctions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseChunck: (chunk: unknown, source: string, errors: unknown[], functions: unknown) => any;
  fileUpload: Record<string, unknown>;
  [key: string]: unknown;
};

export function useRedirections(application?: GenericLocalApplicationResult) {
  // #466 - source fiable du nom de l'application : le param de route Vue ,
  // toujours renseigné quand on est sur /applications/:applicationName/...
  // Sert de fallback quand l'objet 'application' n'est pas encore hydraté.
  const route = useRoute();

  const canCreateApplication = (app?: GenericLocalApplicationResult) => {
    const authenticatedUser = services.loginService.getAuthenticatedUser();
    const appName = app ? getGenericApplicationName(app) : application ? getGenericApplicationName(application) : "";
    if (!appName) {
      return true;
    }
    return services.loginService.isApplicationCreator(appName, authenticatedUser);
  };

  // Référence du fichier temporaire de charte
  const tmpCharte = ref<File | undefined>();

  function createApplication(applicationNames: string[]) {
    return (
      canCreateApplication()
      && router.push({
        path: "/applicationCreation",
        query: { applicationNames },
      })
    );
  }

  function updateApplication(applicationName: string, applicationVersion: string) {
    return (
      canCreateApplication()
      && router.push(`/applicationCreation/${applicationName}/${applicationVersion}`)
    );
  }

  const zipFile = ref<File | null>();

  async function onZipSelected(functions?: FileUploadFunctions) {
    const file = zipFile.value;
    if (!file || !application) {
      return;
    }
    if (!functions) {
      return;
    }
    await uploadApplicationUploadBundle(application, file, functions);
    zipFile.value = null;
  }

  async function downloadYamlApplication(app: GenericLocalApplicationResult): Promise<false | void> {
    try {
      const configFile = getGenericApplicationConfigFile(app);
      return await (canCreateApplication()
        && services.fileService.download(getGenericApplicationName(app), configFile));
    }
    catch (e) {
      console.error(e);
      return false;
    }
  }

  async function landingPageApplication(app: GenericLocalApplicationResult, same?: () => void): Promise<void | FileUrl> {
    const redirection = same || (() => router.push(`/applications/${getGenericApplicationName(app)}`));
    if (!mustSignCharte(app)) {
      redirection();
    }
    else {
      return await downloadCharteApplication(getGenericApplicationName(app), getGenericApplicationId(app), redirection);
    }
  }

  async function downloadApplicationUploadBundle(app: GenericLocalApplicationResult): Promise<boolean> {
    try {
      return await (canCreateApplication()
        && services.applicationService.downloadApplicationUploadBundle(getGenericApplicationName(app), false));
    }
    catch (e) {
      console.error(e);
      return false;
    }
  }

  async function uploadApplicationUploadBundle(
    app: GenericLocalApplicationResult,
    file: File,
    functions: FileUploadFunctions): Promise<boolean> {
    const canBeShowAndReturnErrorsMessage: unknown[] = [];
    const method = (chunk: unknown) => {
      return functions.parseChunck(chunk, "downloadBundle", canBeShowAndReturnErrorsMessage, functions);
    };
    try {
      if (!canCreateApplication()) return false;
      await services.applicationService.uploadApplicationUploadBundle(
        method, getGenericApplicationName(app), file, functions.fileUpload,
      );
      return true;
    }
    catch {
      return false;
    }
  }

  async function downloadApplicationUploadBundleWithData(app: GenericLocalApplicationResult): Promise<boolean> {
    try {
      return await (canCreateApplication()
        && services.applicationService.downloadApplicationUploadBundle(getGenericApplicationName(app), true));
    }
    catch (e) {
      console.error(e);
      return false;
    }
  }

  async function downloadCharteApplication(
    applicationName: string,
    applicationId: string,
    redirection?: () => void,
  ): Promise<FileUrl> {
    // #466 - on construit le path à partir du nom de l'application ( param
    // de route Vue , toujours présent ) et non de son UUID ( qui peut être
    // vide dans certains scénarios non-admin ). Le backend accepte les deux
    // formats sur cette route ( @PathVariable("nameOrId") ).
    // Si l'objet 'application' n'est pas hydraté au clic , 'applicationName'
    // peut aussi être vide ; on bascule alors sur route.params.applicationName.
    const effectiveName = applicationName || (route?.params?.applicationName as string) || "";
    // #466 - le backend type 'uuids' en Set<UUID> ( AdditionalFilesInfos.java )
    // et Jackson lance une 500 si on lui passe une chaîne non-UUID. Pour la
    // charte , le backend résout le fichier via nameOrId + filetype et ignore
    // 'uuids' ( cf commit 68b9e37d ) , donc on envoie un tableau vide quand
    // on n'a pas un vrai UUID à la main.
    const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(applicationId);
    const uuidsArg = looksLikeUuid ? [applicationId] : [];
    const url = services.additionalFileService.getPath(
      `applications/${effectiveName}/additionalFiles?`
      + new URLSearchParams({
        params: JSON.stringify({
          uuids: uuidsArg,
          filetype: "__charte__", // doit matcher AdditionalFileService.CHARTE côté back
        }),
      }).toString(),
    );

    const response = await services.additionalFileService.get(url.toString()) as Response;
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    let filename = "charte.pdf";
    const contentDisposition = response.headers.get("Content-Disposition");
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) filename = match[1];
    }

    return { url: blobUrl, filename, redirection };
  }

  async function uploadCharteApplication(app: GenericLocalApplicationResult, comment: string) {
    if (await canCreateApplication(app)) {
      await services.additionalFileService.addAdditionalFile(
        getGenericApplicationName(app),
        "__charte__",
        tmpCharte.value,
        {
          id: getGenericApplicationId(app),
          fileType: "__charte__",
          comment,
        },
      );
    }
    services.alertService.toastSuccess("alert.application-charte_upload-success");
    return downloadCharteApplication(getGenericApplicationName(app), getGenericApplicationId(app));
  }

  function displayReferencesManagement(app: GenericLocalApplicationResult) {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(app)}/references`);
    if (!mustSignCharte(app)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(app), getGenericApplicationId(app), redirection);
    }
  }

  function showReferenceRights(app: GenericLocalApplicationResult) {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(app)}/references/authorizations`);
    if (!mustSignCharte(app)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(app), getGenericApplicationId(app), redirection);
    }
  }

  function displayDataSetManagement(app: GenericLocalApplicationResult) {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(app)}/dataTypes`);
    if (!mustSignCharte(app)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(app), getGenericApplicationId(app), redirection);
    }
  }

  function showDataRights(app: GenericLocalApplicationResult) {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(app)}/authorizations`);
    if (!mustSignCharte(app)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(app), getGenericApplicationId(app), redirection);
    }
  }

  function displayAdditionalFilesManagement() {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(application)}/additionalFiles`);
    if (application && !mustSignCharte(application)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(application), getGenericApplicationId(application), redirection);
    }
  }

  function authorizationsManagementForApplicationUsers() {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(application)}/authorizationsManagementForApplicationUsers`);
    if (application && !mustSignCharte(application)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(application), getGenericApplicationId(application), redirection);
    }
  }

  function requestRights() {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(application)}/authorizationsRequest`);
    if (application && !mustSignCharte(application)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(application), getGenericApplicationId(application), redirection);
    }
  }

  function showRequestRights() {
    const redirection = () => router.push(`/applications/${getGenericApplicationName(application)}/authorizationsRequest/new`);
    if (application && !mustSignCharte(application)) {
      redirection();
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(application), getGenericApplicationId(application), redirection);
    }
  }

  function showAdditionalFilesManagementRights() {
    if (application && !mustSignCharte(application)) {
      router.push(`/applications/${getGenericApplicationName(application)}/additionalFiles/authorizations`);
    }
    else {
      return downloadCharteApplication(getGenericApplicationName(application), getGenericApplicationId(application));
    }
  }

  return {
    createApplication,
    updateApplication,
    downloadYamlApplication,
    downloadCharteApplication,
    uploadCharteApplication,
    tmpCharte,
    zipFile,
    onZipSelected,
    displayReferencesManagement,
    showReferenceRights,
    displayDataSetManagement,
    showDataRights,
    displayAdditionalFilesManagement,
    showAdditionalFilesManagementRights,
    requestRights,
    showRequestRights,
    downloadApplicationUploadBundle,
    uploadApplicationUploadBundle,
    downloadApplicationUploadBundleWithData,
    landingPageApplication,
    authorizationsManagementForApplicationUsers,
  };
}
